import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import api from "../../config/URL";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import fetchAllCoursesWithIds from "../List/CourseList";
import fetchAllLevelsWithIds from "../List/LevelList";

function EnrollmentReports({ selectedCenter }) {
  const currentYear = new Date().getFullYear();
  const [coursesData, setCoursesData] = useState(null);
  const [levelsData, setLevelsData] = useState(null);

  const [filters, setFilters] = useState({
    filterKey: "weekly", // default filter
    courseType: "Normal", // default filter
  });

  const [chartData, setChartData] = useState([]);
  const [leadInfo, setLeadInfo] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch enrollment data
  const fetchEnrollmentData = async () => {
    if (!selectedCenter || !filters.filterKey || !filters.courseType) return;

    try {
      setLoading(true);
      const res = await api.get("/globalEnrolmentReport", {
        params: {
          centerId: selectedCenter,
          filterKey: filters.filterKey,
          courseType: filters.courseType,
          courseId: filters.courseId,
          levelId: filters.levelId,
        },
      });
      setChartData(res.data[filters.filterKey] || []);
      setLeadInfo(res.data.leadInfo || []);
    } catch (error) {
      console.error("Error fetching enrollment data:", error);
      toast.error("Error fetching enrollment data");
      setChartData([]);
      setLeadInfo([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoursesData = async () => {
    try {
      const course = await fetchAllCoursesWithIds();
      setCoursesData(course);
    } catch (error) {
      toast.error(error.message || "Error fetching course");
    }
  };

  const fetchLevelData = async () => {
    try {
      const level = await fetchAllLevelsWithIds();
      setLevelsData(level);
    } catch (error) {
      toast.error(error.message || "Error fetching Level");
    }
  };


  useEffect(() => {
    fetchEnrollmentData();
  }, [selectedCenter, filters]);

   useEffect(() => {
    fetchCoursesData();
    fetchLevelData();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Define colors for each lead status
  const statusColors = {
    Trial: "#FF5733",
    Enrolled: "#4CAF50",
  };

  // ===== Enrollment Chart Options =====
  const chartOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { curve: "straight", width: 3, colors: ["#FF5733"] },
    markers: {
      size: 6,
      colors: ["#FF5733"],
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 8 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "12px", colors: ["#FF5733"] },
    },
    title: {
      text: `Enrolment ${currentYear} (${
        filters.filterKey === "monthly" ? "Month-wise" : "Week-wise"
      })`,
      align: "center",
      style: { fontSize: "20px", fontWeight: "bold" },
    },
    xaxis: {
      categories: chartData.map((item) => item.label),
      title: { text: filters.filterKey === "monthly" ? "Month" : "Week" },
    },
    yaxis: { min: 0, title: { text: "Students" } },
    grid: { borderColor: "#e7e7e7" },
  };

  const chartSeries = [
    {
      name: "Students",
      data: chartData.map((item) => item.studentCount),
    },
  ];

  // ===== Lead Info Chart Options (Modified to Line Chart) =====
  const leadChartOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: {
      curve: "straight",
      width: 3,
      colors: Object.values(statusColors),
    },
    markers: {
      size: 6,
      colors: Object.values(statusColors),
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 8 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "12px", colors: Object.values(statusColors) },
    },
    title: {
      text: `Lead Info ${currentYear} (${
        filters.filterKey === "monthly" ? "Month-wise" : "Week-wise"
      })`,
      align: "center",
      style: { fontSize: "20px", fontWeight: "bold" },
    },
    xaxis: {
      categories: leadInfo.map((item) => item.label),
      title: { text: filters.filterKey === "monthly" ? "Month" : "Week" },
    },
    yaxis: { min: 0, title: { text: "Count" } },
    grid: { borderColor: "#e7e7e7" },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  const leadChartSeries = Object.keys(statusColors).map((status) => ({
    name: status,
    data: leadInfo.map((item) => item[status] || 0),
  }));

  return (
    <div className="p-1">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Report Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Enrollment Report
        </li>
      </ol>

      {/* Filters */}
      <div
        className="d-flex justify-content-between align-items-center p-2"
        style={{ gap: "10px" }}
      >
        {/* Hidden Center Input */}
        <input name="centerId" hidden value={selectedCenter} />

        {/* Filter Type Select */}
        <select
          className="form-select form-select-sm w-30"
          onChange={(e) => handleFilterChange("filterKey", e.target.value)}
          value={filters.filterKey}
        >
          <option value="monthly">Monthly</option>
          <option value="weekly">Weekly</option>
        </select>

        <select
          className="form-select form-select-sm w-30"
          onChange={(e) => handleFilterChange("courseType", e.target.value)}
          value={filters.courseType}
        >
          <option value=""></option>
          <option value="Normal">Normal</option>
          <option value="Holiday">Holiday</option>
        </select>

         <select
          className="form-select form-select-sm w-30"
          name="courseId"
          style={{ width: "100%" }}
          onChange={(e) => handleFilterChange("courseId", e.target.value)}
          value={filters.courseId}
        >
          <option value="" disabled>
            Select a Course
          </option>
          {coursesData?.map((course) => (
            <option key={course.id} value={course.id}>
              {course.courseNames}
            </option>
          ))}
        </select>

         <select
          className="form-select form-select-sm w-30"
          name="levelId"
          style={{ width: "100%" }}
          onChange={(e) => handleFilterChange("levelId", e.target.value)}
          value={filters.levelId}
        >
          <option value="" disabled>
            Select a Level
          </option>
          {levelsData?.map((level) => (
            <option key={level.id} value={level.id}>
              {level.levels}
            </option>
          ))}
        </select>
      </div>

      {/* Enrollment Chart */}
      {loading ? (
        <div className="loader-container">
          <div className="loading">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      ) : (
        <>
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={350}
          />

          {/* Lead Info Chart Below */}
          {leadInfo.length > 0 && (
            <Chart
              options={leadChartOptions}
              series={leadChartSeries}
              type="line"
              height={350}
            />
          )}
        </>
      )}
    </div>
  );
}

export default EnrollmentReports;